/**
 * chat_react_wrap_controller.js — Stimulus shell for the Team Chat Preact app.
 *
 * Fullscreen mode: renders the complete chat UI (sidebar + messages) as a single
 * Preact/Signals tree. The sidebar room list and message panel are both Preact
 * components — no server-side rendered sidebar.
 *
 * Room navigation is fully client-side:
 *   - On mount the controller reads the `room_id` query param (or data attribute)
 *     to open the initial room.
 *   - When the user clicks a room in the Preact sidebar, the Preact component
 *     calls `store.navigateToRoom(id)` which updates the signal AND pushes the
 *     URL state via pushState.
 *   - Browser back/forward is supported via popstate listener.
 */

import { Controller } from "@hotwired/stimulus"
import { h, render }  from 'preact'
import { createChatStore }  from '../modules/chat/chat-store'
import { connectChatCable } from '../modules/chat/chat-cable'
import { configureChatApi } from '../modules/chat/chat-api'
import { ChatApp }          from '../modules/chat/components/ChatApp'

export default class extends Controller {
  static values = {
    roomId:         { type: Number, default: 0 },
    personId:       { type: Number, default: 0 },
    personName:     { type: String, default: '' },
    tenantId:       { type: String, default: '' },
    mode:           { type: String, default: 'fullscreen' },
    canManageRooms: { type: Boolean, default: false },
    logoUrl:        { type: String, default: '' },
    showBack:       { type: Boolean, default: false },
  }

  connect() {
    configureChatApi({
      tenantId: this.tenantIdValue,
      personId: this.personIdValue,
    })

    // Determine initial room: URL query param takes precedence over data attribute
    const urlRoomId = this._roomIdFromUrl()
    const initialRoomId = urlRoomId || this.roomIdValue || null

    this.store = createChatStore({
      roomId:         initialRoomId,
      personId:       this.personIdValue,
      personName:     this.personNameValue,
      tenantId:       this.tenantIdValue,
      canManageRooms: this.canManageRoomsValue,
    })

    // Restore float panels from localStorage BEFORE mounting
    if (this.personIdValue) {
      try {
        const key    = `tsh:chatDock:${this.personIdValue}.panels`
        const stored = localStorage.getItem(key)
        if (stored) {
          const parsed = JSON.parse(stored)
          if (Array.isArray(parsed)) this.store.floatPanels.value = parsed
        }
      } catch { /* ignore */ }
    }

    this.cable = connectChatCable({
      store:    this.store,
      personId: this.personIdValue,
      tenantId: this.tenantIdValue,
    })

    // Attach pushState-based navigation to the store so Preact components can call it
    this.store.navigateToRoom = (roomId) => {
      this.store.activeRoomId.value = roomId
      if (this.modeValue === 'fullscreen') {
        const url = roomId
          ? `/chat/rooms?room_id=${roomId}`
          : '/chat/rooms'
        window.history.pushState({ roomId }, '', url)
      }
    }

    // Handle browser back/forward navigation
    this._onPopState = (event) => {
      const roomId = event.state?.roomId || this._roomIdFromUrl() || null
      this.store.activeRoomId.value = roomId
    }
    window.addEventListener('popstate', this._onPopState)

    // Mount the Preact app — it renders the complete UI
    render(
      h(ChatApp, {
        store: this.store,
        cable: this.cable,
        mode: this.modeValue,
        logoUrl: this.logoUrlValue,
        showBack: this.showBackValue,
      }),
      this.element
    )

    // Subscribe to the initial room's cable channel if one is pre-selected
    if (initialRoomId) {
      this.cable.subscribeRoom(initialRoomId)
    }
  }

  disconnect() {
    window.removeEventListener('popstate', this._onPopState)
    this.cable?.disconnect()
    render(null, this.element)
  }

  // ── Private helpers ────────────────────────────────────────────────────

  _roomIdFromUrl() {
    const params = new URLSearchParams(window.location.search)
    const id = parseInt(params.get('room_id'), 10)
    return isNaN(id) ? null : id
  }
}
